import { DOCUMENT } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { AfterViewChecked, AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { CoreService } from "@app/core/services/core.service";
import { IndicativeService } from "@app/core/services/indicative.service";
import { MetaService } from "@app/core/services/meta.service";
import { SessionService } from "@app/core/services/session.service";
import { UrlConfig, getPortalUrl, isClientSideExecution, isCn } from "@app/core/utils";
import { environment } from "@env/environment";
import { TranslateService } from "@ngx-translate/core";
import jwt_decode from "jwt-decode";
import { BehaviorSubject, Subscription, map, timer } from "rxjs";
import { AuthenticationService } from "./authentication.service";

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
    showPassword: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    emailAddressEmpty: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    emailAddress: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    password: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    code: string;

    initializeGoogleSignInSdkSubscription: Subscription;
    isGoogleSignInInitialized: boolean = false;
    isGoogleSignInButtonRendering: boolean = false;
    isGoogleSignInButtonRendered: boolean = false;
    renderGoogleSignInButtonSubscription: Subscription;
    year: string = new Date().getFullYear().toString();

    userIsBot: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    intervalSubscription: Subscription;

    @ViewChild("googleSignIn") googleSignIn: ElementRef = new ElementRef({});

    constructor(
        public authenticationService: AuthenticationService,
        public sessionService: SessionService,
        private router: Router,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private translate: TranslateService,
        private indicativeService: IndicativeService,
        private metaService: MetaService,
        private coreService: CoreService,
        private http: HttpClient,
        @Inject(DOCUMENT) private document: any
    ) {}

    ngOnInit(): void {
        this.authenticationService.resetErrors();

        this.isGoogleSignInInitialized = false;
        this.isGoogleSignInButtonRendering = false;
        this.isGoogleSignInButtonRendered = false;

        if (isClientSideExecution()) {
            this.authenticationService.getFingerprintDevice();

            this.authenticationService.loading.subscribe((loading: boolean) => {
                if (!loading) {
                    this.renderGoogleSignInButton();
                }
            });

            this.translate.onLangChange.subscribe({
                next: () => {
                    this.renderGoogleSignInButton();
                },
            });
        }

        if (this.sessionService.sessionData.value) {
            if (isClientSideExecution()) {
                window.location.href = getPortalUrl();
            }
        }

        this.indicativeService.sendEvent("login_visited");
    }

    ngAfterViewInit(): void {
        this.setMetaData();
        this.loadGoogleSignIn();
        this.coreService.loadGoogleReCaptcha();
    }

    ngAfterViewChecked() {
        if (!isClientSideExecution()) {
            return;
        }
    }

    ngOnDestroy() {
        this.intervalSubscription?.unsubscribe();
    }

    loadGoogleSignIn() {
        if (!isClientSideExecution()) {
            return;
        }

        if (this.isGoogleSignInInitialized) {
            return;
        }

        this.coreService.loadGoogleSignInScript();

        this.initializeGoogleSignInSdkSubscription = timer(0, 500)
            .pipe(
                map(() => {
                    if ((<any>window).google) {
                        (<any>window).google.accounts.id.initialize({
                            client_id: environment.production
                                ? "643729411266-0m6a7qash1j17l8lahbm09btrffi7p6p.apps.googleusercontent.com"
                                : "643729411266-esjsqmk7imfdeip55pt34fcjso7g3vs8.apps.googleusercontent.com",
                            callback: this.googleSignInCallback.bind(this),
                        });

                        this.isGoogleSignInInitialized = true;
                        this.renderGoogleSignInButton();
                        this.initializeGoogleSignInSdkSubscription.unsubscribe();
                    }
                })
            )
            .subscribe();
    }

    renderGoogleSignInButton() {
        this.isGoogleSignInButtonRendered = false;

        this.renderGoogleSignInButtonSubscription = timer(0, 1000)
            .pipe(
                map(() => {
                    if (this.googleSignIn && this.googleSignIn.nativeElement) {
                        if (this.isGoogleSignInButtonRendered) {
                            return;
                        }

                        if (!this.isGoogleSignInInitialized) {
                            return;
                        }

                        if (this.isGoogleSignInButtonRendering) {
                            return;
                        }

                        if ((<any>window).google) {
                            this.isGoogleSignInButtonRendering = true;

                            (<any>window).google.accounts.id.renderButton(this.googleSignIn.nativeElement, {
                                type: "standard",
                                theme: "outline",
                                text: "signin",
                                size: "large",
                                width: "220px",
                                shape: "rectangular",
                                ux_mode: "popup",
                                logo_alignment: "left",
                                locale: this.coreService.getLanguage(),
                            });
                            this.isGoogleSignInButtonRendering = false;
                            this.isGoogleSignInButtonRendered = true;
                            this.renderGoogleSignInButtonSubscription.unsubscribe();
                        }
                    }
                })
            )
            .subscribe();
    }

    googleSignInCallback(response) {
        this.indicativeService.sendEvent("login_interacting");
        this.indicativeService.sendEvent("login_google_interacting");

        this.authenticationService.confirmationRequired.subscribe({
            next: (res: any) => {
                if (res) {
                    this.router.navigate(["confirm"], {
                        relativeTo: this.route,
                    });
                }
            },
        });

        const decodedJwt: any = jwt_decode(response.credential);

        if (!decodedJwt.email) {
            this.indicativeService.sendEvent("login_fail");
            this.indicativeService.sendEvent("login_google_fail");
            this.authenticationService.generalLoginError.next(true);
            this.authenticationService.loading.next(false);
            this.http
                .post(`${environment.baseNodePathForRequests}/api/utils/sentry/google_login`, {
                    error: `Response from Google without email address. ${JSON.stringify(decodedJwt)}`,
                })
                .subscribe({
                    next: (resp: any) => {},
                });
            return;
        }

        const authenticationService: AuthenticationService = this.authenticationService;
        const emailAddress: string = decodedJwt.email;
        const name: string = decodedJwt.name;
        const idToken: string = response.credential;

        if ((<any>window).grecaptcha === undefined || !(<any>window).grecaptcha) {
            this.coreService.loadGoogleReCaptcha();
            authenticationService.specificLoginError.next("Invalid reCAPTCHA token.");
            return;
        }

        (<any>window).grecaptcha.ready(() => {
            (<any>window).grecaptcha
                .execute(environment.googleReCaptchSiteId, { action: "submit" })
                .then((token) => {
                    authenticationService.loginWithGoogle(emailAddress, name, idToken, token);
                })
                .catch(() => {
                    authenticationService.specificLoginError.next("Invalid reCAPTCHA token.");
                });
        });
    }

    private setMetaData() {
        this.metaService.setMetadata(
            this.metaService.generatePageMetaObject(
                `Login on Aptoide Connect`,
                "Login on Aptoide Connect and unleash your applications in Aptoide and over 10 App Stores!",
                UrlConfig.getURL(this.router.url),
                "",
                this.router.url
            )
        );
    }

    updateEmailAddress(evt: string) {
        this.indicativeService.sendEvent("login_interacting");
        this.indicativeService.sendEvent("login_password_interacting");
        this.emailAddress.next(evt);
    }

    updatePassword(evt: any) {
        this.indicativeService.sendEvent("login_interacting");
        this.indicativeService.sendEvent("login_password_interacting");
        this.password.next(evt);
    }

    logIn() {
        this.emailAddressEmpty.next(false);

        if (!this.emailAddress.value || !this.password.value) {
            this.emailAddressEmpty.next(true);
            return;
        }

        this.authenticationService.confirmationRequired.subscribe({
            next: (res: any) => {
                if (res) {
                    this.router.navigate(["confirm"], {
                        relativeTo: this.route,
                    });
                }
            },
        });

        const authenticationService: AuthenticationService = this.authenticationService;
        const emailAddress: string = this.emailAddress.value;
        const password: string = this.password.value;

        if ((<any>window).grecaptcha === undefined || !(<any>window).grecaptcha) {
            this.coreService.loadGoogleReCaptcha();
            authenticationService.specificLoginError.next("Invalid reCAPTCHA token.");
            return;
        }

        (<any>window).grecaptcha.ready(() => {
            (<any>window).grecaptcha
                .execute(environment.googleReCaptchSiteId, { action: "submit" })
                .then((token) => {
                    authenticationService.loginWithEmailAddressPassword(emailAddress, password, token);
                })
                .catch(() => {
                    authenticationService.specificLoginError.next("Invalid reCAPTCHA token.");
                });
        });
    }

    togglePasswordVisibility() {
        if (!this.authenticationService.loading.value) {
            this.showPassword.next(!this.showPassword.value);
        }
    }

    preventFocus(event) {
        event.preventDefault();
    }
}
